import { Capacitor } from '@capacitor/core';
import { useEffect, useState } from 'react';

/** True when running inside a Capacitor native shell (iOS or Android). */
export const isNative = Capacitor.isNativePlatform();

/** True when running inside iOS native app. */
export const isIOS = Capacitor.getPlatform() === 'ios';

/** True when running inside Android native app. */
export const isAndroid = Capacitor.getPlatform() === 'android';

/** True when running in a web browser (not native). */
export const isWeb = !isNative;

export interface DeviceInfo {
  platform: 'ios' | 'android' | 'web';
  model: string | null;
  osVersion: string | null;
}

/** Returns basic device info. Falls back to web values if Device plugin unavailable. */
export async function getDeviceInfo(): Promise<DeviceInfo> {
  if (!isNative) {
    return { platform: 'web', model: null, osVersion: null };
  }
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    return {
      platform: info.platform as 'ios' | 'android',
      model:     info.model ?? null,
      osVersion: info.osVersion ?? null,
    };
  } catch {
    return { platform: isIOS ? 'ios' : 'android', model: null, osVersion: null };
  }
}

/** React hook exposing platform flags. */
export function usePlatform() {
  const [info, setInfo] = useState({ isNative, isIOS, isAndroid, isWeb });
  useEffect(() => { setInfo({ isNative, isIOS, isAndroid, isWeb }); }, []);
  return info;
}
